import type { ReactNode } from 'react'
import styles from './DocumentDetailsModal.module.css'
import {
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheet,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { FileTypeIcon } from '@/components/FileTypeIcon'
import { getFileIconType } from '@/utils/icon-util'
import { TabBar, TabItem } from '@/components/TabBar'

export interface DocumentDetailsModalProps {
  showing: boolean
  onClose: () => unknown
  fileType: string
  headerTitle: string
  backingFileName: string
  documentTypeName?: string
  canEdit: boolean
  canRemove: boolean
  canDownload: boolean
  isDetailsTab: boolean
  onSelectDetailsTab: () => unknown
  hasChanges: boolean
  isPending: boolean
  isSavePending: boolean
  onDiscardChanges: () => unknown
  onDelete: () => unknown
  onDownload: () => unknown
  onSave: () => unknown
  /**
   * Content of the active tab.
   */
  children?: ReactNode
}

export function DocumentDetailsModal({
  showing,
  onClose,
  fileType,
  headerTitle,
  backingFileName,
  documentTypeName,
  canEdit,
  canRemove,
  canDownload,
  isDetailsTab,
  onSelectDetailsTab,
  hasChanges,
  isPending,
  isSavePending,
  onDiscardChanges,
  onDelete,
  onDownload,
  onSave,
  children,
}: DocumentDetailsModalProps) {
  const icon = getFileIconType(fileType)

  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSavePending ? undefined : onClose}
    >
      <ModalSheetHeaderShell size="auto" bordered>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div>
              <FileTypeIcon
                height={36}
                type={icon.type}
                primaryColor={icon.color}
                strokeColor="#bcbcbe"
              />
            </div>
            <div className={styles.headerInfo}>
              <div className={styles.headerTitle}>
                <span>{headerTitle}</span>
                {!canEdit && (
                  <span className={styles.readOnlyBadge}>Read Only</span>
                )}
              </div>
              {headerTitle !== backingFileName && (
                <div className={styles.headerDescription}>
                  <em>{backingFileName}</em>
                </div>
              )}
              {documentTypeName !== undefined && (
                <div className={styles.headerDescription}>
                  {documentTypeName}
                </div>
              )}
            </div>
          </div>
          <div className={styles.tabContainer}>
            <TabBar align="left">
              <TabItem
                active={isDetailsTab}
                onPress={() => onSelectDetailsTab()}
              >
                Details
              </TabItem>
            </TabBar>
          </div>
        </div>
        <div className={styles.closeBtn}>
          <ModalSheetHeaderCloseButton />
        </div>
      </ModalSheetHeaderShell>
      <ModalSheetContent>{children}</ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterButtonGroup>
          {canEdit && canRemove && (
            <>
              {hasChanges ? (
                <Button
                  label="Discard Changes"
                  isDisabled={isPending}
                  onPress={() => onDiscardChanges()}
                />
              ) : (
                <Button
                  label="Delete"
                  isDisabled={isPending}
                  onPress={() => onDelete()}
                />
              )}
            </>
          )}
        </ModalSheetFooterButtonGroup>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {canDownload && (
            <Button
              label="Download"
              isDisabled={isSavePending}
              onPress={onDownload}
            />
          )}
          {canEdit && isDetailsTab && (
            <Button
              label="Save"
              variant="primary"
              isDisabled={!hasChanges}
              isPending={isSavePending}
              onPress={onSave}
            />
          )}
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
